import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ClientLayout from '../../components/client/ClientLayout';
import { getCurrentUser } from '../../lib/auth';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingAsset, setDownloadingAsset] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const fetchUserInfo = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await fetch('/client/api/user/me', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  }, []);

  const fetchProjectDetails = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await fetch(`/client/api/client/projects/${projectId}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Project not found or access denied');
        }
        throw new Error('Failed to fetch project details');
      }
      const data = await response.json();
      setProject(data);
      setAssets(data.assets || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const checkAuthAndLoadData = useCallback(async () => {
    try {
      // Check if user is authenticated via Cloudflare Access
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        // No authentication found - this shouldn't happen since /client* is protected
        // Cloudflare Access should have intercepted before reaching here
        window.location.href = '/client';
        return;
      }
      
      // User is authenticated, load data
      await fetchUserInfo();
      await fetchProjectDetails();
    } catch (error) {
      console.error('Authentication check failed:', error);
      window.location.href = '/client';
    }
  }, [fetchUserInfo, fetchProjectDetails]);

  useEffect(() => {
    checkAuthAndLoadData();
  }, [checkAuthAndLoadData]);

  const getFileIcon = (kind) => {
    switch (kind) {
      case 'video':
        return <span className="text-2xl">🎥</span>;
      case 'photo':
        return <span className="text-2xl">🖼️</span>;
      case 'zip':
        return <span className="text-2xl">📦</span>;
      case 'doc':
        return <span className="text-2xl">📄</span>;
      default:
        return <span className="text-2xl">📁</span>;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = async () => {
    try {
      // First, log the logout action
      const response = await fetch('/client/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Cloudflare Access logout with redirect to main website
        const returnUrl = encodeURIComponent(window.location.origin);
        window.location.href = `${data.redirect_url || '/cdn-cgi/access/logout'}?return_to=${returnUrl}`;
      } else {
        // If logging fails, still redirect to logout
        window.location.href = '/cdn-cgi/access/logout?return_to=' + encodeURIComponent(window.location.origin);
      }
    } catch (error) {
      console.error('Failed to log logout:', error);
      // If logging fails, still redirect to logout
      window.location.href = '/cdn-cgi/access/logout?return_to=' + encodeURIComponent(window.location.origin);
    }
  };

  const handleDownload = async (asset) => {
    setDownloadingAsset(asset.id);
    try {
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        throw new Error('User is not authenticated');
      }

      // Get download URL from the API
      const response = await fetch(`/client/api/client/assets/${asset.id}/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate download link');
      }
      
      const data = await response.json();
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = data.download_url;
      link.download = asset.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Download initiated silently - no message needed
      
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingAsset(null);
    }
  };

  if (loading) {
    return (
      <ClientLayout userInfo={userInfo} onLogout={handleLogout}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto"></div>
            <p className="mt-4 text-brand-dark">Loading project details...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout userInfo={userInfo} onLogout={handleLogout}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-sm">
              <p className="font-semibold">Error Loading Project</p>
              <p className="mt-1">{error}</p>
            </div>
            <button 
              onClick={() => navigate('/client')} 
              className="mt-4 bg-brand-blue-600 text-white px-6 py-2 rounded-md hover:bg-brand-blue-700 transition-colors shadow-sm"
            >
              Back to Projects
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout userInfo={userInfo} onLogout={handleLogout}>
      {/* Breadcrumb & Project Header */}
      <div className="bg-brand-black shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/client')}
              className="flex items-center text-white hover:text-ink mr-6 transition-colors"
            >
              <span className="mr-2">←</span>
              Back to Projects
            </button>
          </div>
          
          <div className="flex items-center">
            <div className="h-12 w-12 text-white mr-4 flex items-center justify-center text-3xl bg-white/10 rounded-xl">
              📁
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">{project?.title}</h1>
              <p className="text-lg text-ink mt-1">{project?.client_name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {/* Project Info */}
        {project?.notes && (
          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Project Description</h2>
            <p className="text-gray-600">{project.notes}</p>
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <span className="mr-1">📅</span>
              <span>Created {formatDate(project.created_at)}</span>
            </div>
          </div>
        )}

        {/* Assets List */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Project Files</h2>
              <p className="text-xs text-gray-500">
                Download links expire after 15 minutes
              </p>
            </div>
          </div>
          
          {assets.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center text-3xl">
                📁
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No deliverables yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Your project files will appear here once they're uploaded.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {assets.map((asset) => (
                <div key={asset.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className="flex-shrink-0 mr-4">
                        {getFileIcon(asset.kind)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {asset.filename}
                        </h3>
                        {asset.description && (
                          <p className="text-sm text-gray-700 mt-1">
                            {asset.description}
                          </p>
                        )}
                        <div className="flex items-center mt-1 text-sm text-gray-500">
                          <span>{formatFileSize(asset.size_bytes)}</span>
                          <span className="mx-2">•</span>
                          <span>Uploaded {formatDate(asset.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <button
                        onClick={() => handleDownload(asset)}
                        disabled={downloadingAsset === asset.id}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingAsset === asset.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Preparing...
                          </>
                        ) : (
                          <>
                            <span className="mr-2">⬇️</span>
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Download Disclaimer */}
        <div className="mt-8 bg-brand-blue-600 border border-brand-blue-700 rounded-xl p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="h-6 w-6 text-white flex items-center justify-center">
                🔒
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-white">Security Notice</h3>
              <div className="mt-2 text-sm text-white/90">
                <p className="font-medium mb-1">
                  For your security, download links expire after 15 minutes.
                </p>
                <p>
                  If a link stops working, simply click the download button again to get a new one.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ProjectDetails;
